<template>
  <section class="before-after-section" ref="root">
    <header class="ba-header" ref="head">
      <h2 class="ba-title">Our Cleaning Results</h2>
      <p class="ba-subtitle">Some real before and after images of our services</p>
    </header>

    <div class="ba-carousel">
      <div
        class="ba-slide"
        v-for="(item, i) in items"
        :key="i"
        :ref="el => slides[i] = el"
      >
        <div class="ba-images">
          <figure class="ba-figure" @click="openLightbox(item.before)">
            <img :src="item.before" alt="Before" />
            <figcaption>Before</figcaption>
          </figure>
          <figure class="ba-figure" @click="openLightbox(item.after)">
            <img :src="item.after" alt="After" />
            <figcaption>After</figcaption>
          </figure>
        </div>
        <h3 class="ba-caption">{{ item.title }}</h3>
      </div>
    </div>

    <!-- Lightbox -->
    <div v-if="lightboxImg" class="lightbox" @click="closeLightbox">
      <img :src="lightboxImg" alt="Enlarged view" />
    </div>
  </section>
</template>

<script>
import { ref, onMounted, onBeforeUnmount, nextTick } from 'vue'
import { gsap } from 'gsap'

export default {
  name: "BeforeAfterSection",
  setup() {
    const root = ref(null)
    const head = ref(null)
    const slides = ref([])
    const lightboxImg = ref(null)

    // ⚠️ Cambia estas rutas por tus imágenes reales
    const items = ref([
      {
        title: "Cocina",
        before: require("@/assets/antes1.jpg"),
        after: require("@/assets/despues1.jpg")
      },
      {
        title: "Baños",
        before: require("@/assets/antes2.jpg"),
        after: require("@/assets/despues2.jpg")
      },
      {
        title: "Salas",
        before: require("@/assets/antes3.jpg"),
        after: require("@/assets/despues3.jpg")
      }
    ])

    let ctx = null
    let headerObserver = null
    let slideObserver = null

    const openLightbox = (img) => { lightboxImg.value = img }
    const closeLightbox = () => { lightboxImg.value = null }

    onMounted(async () => {
      await nextTick()

      // Contexto GSAP para revertir estilos al desmontar
      ctx = gsap.context(() => {
        // Estado inicial
        gsap.set(head.value, { autoAlpha: 0, y: 24 })
        gsap.set(slides.value.filter(Boolean), { autoAlpha: 0, y: 32 })
        gsap.set('.ba-figure img', { scale: 1.06 })
      }, root)

      // Observer para el header (una vez)
      headerObserver = new IntersectionObserver((entries) => {
        entries.forEach(e => {
          if (!e.isIntersecting) return
          gsap.to(head.value, { autoAlpha: 1, y: 0, duration: 0.6, ease: 'power2.out' })
          headerObserver && headerObserver.disconnect()
        })
      }, { threshold: 0.25 })
      headerObserver.observe(head.value)

      // Observer para cada slide (secuencial cuando entren)
      slideObserver = new IntersectionObserver((entries) => {
        entries.forEach(e => {
          if (!e.isIntersecting) return

          const el = e.target
          // Animación escalonada interna por tarjeta
          const figs = el.querySelectorAll('.ba-figure')
          const caption = el.querySelector('.ba-caption')
          const imgs = el.querySelectorAll('.ba-figure img')

          const tl = gsap.timeline({ defaults: { ease: 'power2.out' } })
          tl.to(el, { autoAlpha: 1, y: 0, duration: 0.55 })
            .to(imgs, { scale: 1, duration: 0.6, stagger: 0.05 }, '<')
            .from(figs, { y: 14, autoAlpha: 0, duration: 0.4, stagger: 0.08 }, '<0.05')
            .from(caption, { y: 12, autoAlpha: 0, duration: 0.4 }, '-=0.2')

          slideObserver.unobserve(el) // no repetir
        })
      }, { rootMargin: '0px 0px -10% 0px', threshold: 0.2 })

      slides.value.filter(Boolean).forEach(s => slideObserver.observe(s))
    })

    onBeforeUnmount(() => {
      if (headerObserver) headerObserver.disconnect()
      if (slideObserver) slideObserver.disconnect()
      if (ctx) ctx.revert()
    })

    return {
      root,
      head,
      slides,
      items,
      lightboxImg,
      openLightbox,
      closeLightbox
    }
  }
}
</script>

<style scoped>
.before-after-section {
  background: #f8f9fa;
  padding: 60px 20px;
  text-align: center;
  font-family: 'Poppins', sans-serif;
}

.ba-header {
  margin-bottom: 40px;
}

.ba-title {
  font-size: 2.2rem;
  font-weight: 800;
  color: #122b46;
}

.ba-subtitle {
  font-size: 1rem;
  color: #445066;
}

.ba-carousel {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
  gap: 30px;
width:80hv;  margin: 0 auto;
}

.ba-slide {
  background: white;
  border-radius: 12px;
  box-shadow: 0 8px 20px rgba(0,0,0,0.08);
  padding: 20px;
  will-change: transform, opacity;
}

.ba-images {
  display: flex;
  justify-content: space-between;
  gap: 10px;
}

.ba-figure {
  flex: 1;
  border-radius: 8px;
  overflow: hidden;
  background: #eee;
  cursor: zoom-in;
}

.ba-figure img {
  width: 100%;
  height: 200px;
  object-fit: cover;
  display: block;
  transition: transform 0.3s ease;
}

.ba-figure:hover img {
  transform: scale(1.05);
}

.ba-figure figcaption {
  font-size: 0.85rem;
  font-weight: 600;
  color: #333;
  margin-top: 6px;
}

.ba-caption {
  margin-top: 15px;
  font-weight: 700;
  font-size: 1rem;
  color: #1a2b3c;
}

/* Lightbox */
.lightbox {
  position: fixed;
  inset: 0;
  background: rgba(0,0,0,0.85);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 9999;
  cursor: zoom-out;
}

.lightbox img {
  max-width: 90%;
  max-height: 90%;
  border-radius: 10px;
  box-shadow: 0 10px 30px rgba(0,0,0,0.5);
}
</style>
